/**
 * <our-store> — sections/our-store.liquid
 * Reveal on scroll plus a scroll-linked drift on the side column, which travels
 * down as the section scrolls up and retraces its path on the way back.
 */
(function () {
  if (customElements.get("our-store")) return;

  const DESKTOP_QUERY = "(min-width: 1200px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  class OurStore extends HTMLElement {
    connectedCallback() {
      this.panels = Array.from(this.querySelectorAll(".ost_reveal"));
      this.parallax = this.querySelector("[data-ost-parallax]");

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.onScroll = this.onScroll.bind(this);
      this.onModeChange = this.onModeChange.bind(this);

      this.setupReveal();
      this.setupParallax();
    }

    disconnectedCallback() {
      if (this.revealObserver) this.revealObserver.disconnect();
      this.teardownParallax();
      this.desktopQuery?.removeEventListener("change", this.onModeChange);
      this.reducedMotionQuery?.removeEventListener("change", this.onModeChange);
    }

    setupReveal() {
      if (!this.panels.length) return;

      if (this.reducedMotionQuery.matches || !("IntersectionObserver" in window)) {
        this.panels.forEach((panel) => panel.classList.add("is-in-view"));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in-view");
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
      );

      this.panels.forEach((panel) => this.revealObserver.observe(panel));
    }

    setupParallax() {
      this.travel = parseFloat(this.dataset.travel);
      if (!Number.isFinite(this.travel)) this.travel = 0;

      this.desktopQuery.addEventListener("change", this.onModeChange);
      this.reducedMotionQuery.addEventListener("change", this.onModeChange);

      this.enterCurrentMode();
    }

    get wantsParallax() {
      return (
        this.travel > 0 &&
        !!this.parallax &&
        this.desktopQuery.matches &&
        !this.reducedMotionQuery.matches
      );
    }

    onModeChange() {
      this.teardownParallax();
      this.enterCurrentMode();
    }

    enterCurrentMode() {
      if (!this.wantsParallax) {
        if (this.parallax) this.parallax.style.transform = "";
        return;
      }

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });
      this.render();
    }

    teardownParallax() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    onScroll() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.render();
      });
    }

    /* Progress runs 0 → 1 while the section crosses the viewport, so the drift
       simply reverses when the visitor scrolls back up. */
    render() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const span = rect.height + viewport;

      if (span <= 0) return;

      const progress = clamp((viewport - rect.top) / span, 0, 1);
      const offset = (progress - 0.5) * 2 * this.travel;

      this.parallax.style.transform = "translate3d(0, " + offset.toFixed(2) + "px, 0)";
    }
  }

  customElements.define("our-store", OurStore);
})();
